/**
 * Recovered edits — a right-side drawer listing lines that were overwritten by
 * a same-line conflict on another device/co-writer. Each lost line can be
 * cherry-picked back into the current draft. Brand: never says "AI".
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, History, Undo2, Loader2, Trash2 } from "lucide-react";

const TYPE_LABEL = {
  scene_heading: "Scene heading", action: "Action", character: "Character",
  dialogue: "Dialogue", parenthetical: "Parenthetical", transition: "Transition",
  shot: "Shot", subheader: "Subheader",
};

function whenLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export function RecoveredEditsPanel({ scriptId, open, onClose, onInsertLine, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/scripts/${scriptId}/conflicts`);
      setItems(data.conflicts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load recovered edits.");
    } finally { setLoading(false); }
  }, [scriptId]);

  useEffect(() => { if (open && scriptId) load(); }, [open, scriptId, load]);

  const dismiss = async (id) => {
    try {
      await api.delete(`/scripts/${scriptId}/conflicts/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't dismiss that.");
    }
  };

  const insert = (line) => {
    // Intentionally keep the entry visible so a writer can cherry-pick several
    // lines from the same conflict; "Dismiss" clears the whole entry.
    onInsertLine?.(line);
    toast.success("Line restored to your draft.");
  };

  if (!open) return null;

  return (
    <aside
      className="fixed top-0 right-0 z-40 h-full w-[360px] max-w-[90vw] bg-[var(--paper)] border-l border-[var(--line)] shadow-2xl flex flex-col"
      data-testid="recovered-edits-panel"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--line)] shrink-0">
        <div className="flex items-center gap-2">
          <History size={16} className="text-[var(--brand)]" strokeWidth={1.75} />
          <h2 className="font-display text-lg text-[var(--ink-primary)]">Recovered edits</h2>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="recovered-edits-close" title="Close">
          <X size={16} />
        </button>
      </header>

      <div className="px-4 py-3 text-xs text-[var(--ink-muted)] border-b border-[var(--line)]">
        Lines another device or co-writer changed that were overwritten by a save. Bring any of them back into your draft.
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {loading ? (
          <p className="text-sm text-[var(--ink-muted)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[var(--ink-muted)] italic" data-testid="recovered-edits-empty">Nothing here — no edits have been overwritten.</p>
        ) : (
          items.map((entry) => (
            <div key={entry.id} className="rounded-md border border-[var(--line)] bg-[var(--canvas)]" data-testid={`recovered-entry-${entry.id}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--line)]">
                <p className="text-[11px] text-[var(--ink-secondary)] truncate">
                  {entry.lines?.length || 0} line{(entry.lines?.length || 0) === 1 ? "" : "s"} · {entry.resolved_by_name || "Someone"} · {whenLabel(entry.created_at)}
                </p>
                <button onClick={() => dismiss(entry.id)} className="p-1 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)] hover:text-[var(--err)] shrink-0" data-testid={`recovered-dismiss-${entry.id}`} title="Dismiss">
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="divide-y divide-[var(--line)]">
                {(entry.lines || []).map((line, i) => (
                  <div key={`${entry.id}-${i}`} className="px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <p className="text-[11px] font-semibold text-[var(--ink-secondary)] truncate" title={line.scene_heading || `Scene ${line.scene_index}`}>
                        {line.scene_heading ? `Scene ${line.scene_index} · ${line.scene_heading}` : `Scene ${line.scene_index}`}
                      </p>
                      <span className="text-[9px] uppercase tracking-wider text-[var(--ink-muted)] bg-[var(--warm-accent)] px-1.5 py-0.5 rounded shrink-0">
                        {TYPE_LABEL[line.type] || line.type}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--ink-primary)] leading-snug whitespace-pre-wrap break-words mb-2">
                      {line.text || <span className="italic text-[var(--ink-muted)]">(empty line)</span>}
                    </p>
                    <button
                      onClick={() => insert(line)}
                      className="text-[11px] uppercase tracking-wider text-[var(--brand)] hover:underline inline-flex items-center gap-1"
                      data-testid={`recovered-insert-${entry.id}-${i}`}
                    >
                      <Undo2 size={12} /> Insert into draft
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
